import React, { useEffect, useState, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import htmlParse from 'html-react-parser';
import { useTranslation } from "react-i18next";
import { Card, CardBody, Modal, ModalBody, ModalHeader } from "reactstrap";
import authenticatedFetcher from "../../services/fetcher";
import LoadingSpinner from "../LoadingSpinner";
import { toastError, toastSuccess } from "../../utils/ToastConfig";
import { MEDIA_BASE_URL } from '../../config';

export default function CustomModalImportWorkshopCourseAttendant(props) {
  const templateURL = `${MEDIA_BASE_URL}/media/import-templates-examples/plantilla-importacion-talleres-asistentes-y-inscritos.xlsx`;
  const { toggle, open, markHasAttended } = props;
  const [t] = useTranslation("app");
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    course: "",
    college: "",
    workshop: "",
  });
  const [coursesOptions, setCoursesOptions] = useState([]);
  const [collegesOptions, setCollegesOptions] = useState([]);
  const [workshopsOptions, setWorkshopsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    authenticatedFetcher({ path: "/workshops/courses/not-completed/" }).then(
      (res) => Array.isArray(res) && setCoursesOptions(res)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) {
      // Resetear formulario cuando se cierra el modal
      setFormData({ course: "", college: "", workshop: "" });
      setExcelFile(null);
      setCollegesOptions([]);
      setWorkshopsOptions([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, college: "", workshop: "" }));
    setCollegesOptions([]);
    setWorkshopsOptions([]);

    if (formData.course) {
      const selectedCourse = coursesOptions.find(course => course.pk.toString() === formData.course.toString());
      setCollegesOptions(selectedCourse?.colleges || []);
      setWorkshopsOptions(selectedCourse?.workshops || []);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.course]);

  const handleChange = ({ target: { value, name } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = ({ target: { files } }) => {
    if (files && files[0]) {
      setExcelFile(files[0]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!excelFile) {
      toastError("Por favor, seleccione un archivo Excel");
      return;
    }

    setLoadingSubmit(true);
    const formDataToSend = new FormData();
    formDataToSend.append("file", excelFile);
    formDataToSend.append("course", formData.course);
    formDataToSend.append("college", formData.college);
    formDataToSend.append("workshop", formData.workshop);
    if (markHasAttended) {
      formDataToSend.append("mark_has_attended", 1);
    }

    authenticatedFetcher({
      path: "/workshops/attendants/import-massive/",
      method: "POST",
      body: formDataToSend,
      file: true
    })
      .then((res) => {
        if (res.__status || res.error) {
          if (res.error_data && Array.isArray(res.error_data.non_field_errors)) {
            res.error_data.non_field_errors.map((item) => toastError(item));
          } else {
            toastError(res.error || "Error al importar asistentes");
          }
          return false;
        } else {
          const message = res.response || t("save") || "Asistentes importados correctamente";
          if (res.color === "red") {
            toastError(() => htmlParse(message), { autoClose: false });
          } else {
            toastSuccess(() => htmlParse(message), { autoClose: false });
          }
          setLoadingSubmit(false);
          setExcelFile(null);
          setFormData({ course: "", college: "", workshop: "" });
          toggle();
          return true;
        }
      })
      .catch((error) => {
        toastError(error.error || "Error al importar asistentes");
        setLoadingSubmit(false);
      });
  };

  return (
    <Modal size="sm" toggle={toggle} isOpen={open} className="persona">
      <ModalHeader toggle={toggle} charCode="x">
        {markHasAttended ? "Importar asistentes" : "Importar inscritos"}
      </ModalHeader>
      <ModalBody>
        <Card>
          <CardBody>
            <div className="NewPage">
              <div className="card-box card">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-12">
                            <Form.Group className="row">
                              <Form.Label className="BasicLabel right mb-0 col-3">
                                Curso:
                              </Form.Label>
                              <Form.Control
                                className="BasicInput col-9"
                                as="select"
                                name="course"
                                value={formData.course}
                                onChange={handleChange}
                                required
                              >
                                <option value="">Seleccione un curso</option>
                                {coursesOptions.map((item) => (
                                  <option key={item.pk} value={item.pk}>
                                    {item.name || item.title}
                                  </option>
                                ))}
                              </Form.Control>
                            </Form.Group>
                            <Form.Group className="row">
                              <Form.Label className="BasicLabel right mb-0 col-3">
                                Colegio:
                              </Form.Label>
                              {loading ? (
                                <div className="col-9 d-flex align-items-center">
                                  <LoadingSpinner show={loading} />
                                </div>
                              ) : (
                                <Form.Control
                                  className="BasicInput col-9"
                                  as="select"
                                  name="college"
                                  value={formData.college}
                                  onChange={handleChange}
                                  disabled={!formData.course}
                                  required
                                >
                                  <option value="">
                                    {formData.course
                                      ? "Seleccione un colegio"
                                      : "Primero seleccione un curso"}
                                  </option>
                                  {collegesOptions.map((item) => (
                                    <option key={item.pk} value={item.pk}>
                                      {item.name || item.title}
                                    </option>
                                  ))}
                                </Form.Control>
                              )}
                            </Form.Group>
                            <Form.Group className="row">
                              <Form.Label className="BasicLabel right mb-0 col-3">
                                Taller:
                              </Form.Label>
                              {loading ? (
                                <div className="col-9 d-flex align-items-center">
                                  <LoadingSpinner show={loading} />
                                </div>
                              ) : (
                                <Form.Control
                                  className="BasicInput col-9"
                                  as="select"
                                  name="workshop"
                                  value={formData.workshop}
                                  onChange={handleChange}
                                  disabled={!formData.course}
                                  required
                                >
                                  <option value="">
                                    {formData.course
                                      ? "Seleccione un taller"
                                      : "Primero seleccione un curso"}
                                  </option>
                                  {workshopsOptions.map((item) => (
                                    <option key={item.pk} value={item.pk}>
                                      {item.name || item.title}
                                    </option>
                                  ))}
                                </Form.Control>
                              )}
                            </Form.Group>
                            <Form.Group className="row">
                              <Form.Label className="BasicLabel right mb-0 col-3">
                                Archivo Excel:
                              </Form.Label>
                              <div className="col-9">
                                <Form.Control
                                  ref={fileInputRef}
                                  className="BasicInput"
                                  type="file"
                                  accept=".xlsx,.xls"
                                  onChange={handleFileChange}
                                  required
                                />
                                <small className="mt-1 d-block">
                                  <a
                                    href={templateURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                  >
                                    Descargar plantilla de ejemplo
                                  </a>
                                </small>
                                {excelFile && (
                                  <small className="mt-1 d-block text-muted">
                                    Archivo seleccionado: {excelFile.name}
                                  </small>
                                )}
                              </div>
                            </Form.Group>
                          </div>
                        </div>
                        <div className="w-100 my-3">
                          <Button
                            type="submit"
                            variant="secondary ml-auto d-block"
                            className="submitButton"
                            disabled={!formData.course || !formData.college || !formData.workshop || !excelFile || loadingSubmit}
                          >
                            {t("submit")}
                            <LoadingSpinner show={loadingSubmit} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  );
}

CustomModalImportWorkshopCourseAttendant.defaultProps = {
  toggle: () => {},
};
